import React from 'react';
import CTA from "../../components/Constant/cta/CTA";
import WhySection from "../../components/WhySection/WhySection";
import IntroSection from "../../components/IntroSection/IntroSection";
import FAQList from "../../components/Constant/FAQCard/FAQCard";
import Testimonials from "../../components/Constant/Testimonials/Testimonials";
import InfoBlocks from "../../components/InfoBlocks/InfoBlocks";
import ServiceCards from "../../components/ServiceCards/ServiceCards";

export default function FinancialLicense() {

    return (
        <main className='main'>
            <IntroSection
                title="Financial License"
                subtitle="One License. Multiple Financial Services."
                description="The Meta License is a comprehensive, multi-category financial license that allows firms to operate across several regulated sectors under a single umbrella. It is ideal for fintech startups, holding groups, brokerages, crypto exchanges, and gaming operators seeking flexibility, speed, and cost efficiency."
                badges={[
                    "All-in-One License",
                    "Multi-Sector Coverage",
                    "Fast Market Entry"
                ]}
                buttonText="Request a Meta License Proposal →"
                buttonLink="/quote"
                imageSrc={require('../../assets/images/financial-license/financial-license.png')}
                imageAlt="Meta Financial License"
            />

            <section className='section'>
                <div className="container">
                    <WhySection
                        title="Where We Offer Meta Licenses"
                        subtitle='We currently provide Meta License solutions in:'
                        blocks={[
                            {
                                items: [
                                    { text: "Western Sahara (via CRASADR regulator)" },
                                    { text: "Anjouan (Comoros)" },
                                    { text: "Mwali (Comoros)" }
                                ]
                            }
                        ]}
                    />

                    <WhySection
                        title="What Does a Meta License Cover?"
                        subtitle='Depending on the jurisdiction and selected modules, a Meta License can authorize activities across multiple sectors:'
                        blocks={[
                            {
                                items: [
                                    { title: "Banking & Financial Services", text: "Virtual IBANs & SWIFT connectivity, custody & escrow account management, multi-currency corporate accounts." },
                                    { title: "Brokerage & Forex Trading", text: "Online FX/CFD trading, investment & portfolio management, white-label MT5/MetaTrader setup." },
                                    { title: "Crypto & DLT Activities", text: "Centralized & Decentralized Exchanges (CEX/DEX), crypto wallets & token issuance, DeFi projects, NFT marketplaces & custody solutions." },
                                    { title: "EMI / Payment Processing", text: "E-wallets, prepaid cards, remittance, B2B/B2C merchant accounts, API-based PSP infrastructure." },
                                    { title: "Gaming & Betting", text: "Casino, sportsbook, lottery licensing, multi-brand & white-label operations, risk management & player onboarding." },
                                    { title: "Trust, Fund & Asset Management", text: "Fiduciary services & estate structuring, collective investment schemes & family offices, private equity & venture capital vehicles." }
                                ]
                            }
                        ]}
                    />

                    <WhySection
                        title="Why Choose a Meta License?"
                        blocks={[
                            {
                                items: [
                                    { text: "All-in-One Coverage — replace multiple separate licenses with one." },
                                    { text: "Cost-Effective — reduced regulatory & renewal costs." },
                                    { text: "Speed to Market — faster approval than traditional multi-license setups." },
                                    { text: "Flexibility — expand into new sectors without additional licensing." },
                                    { text: "Global Credibility — operate with recognized licensing frameworks." }
                                ]
                            }
                        ]}
                    />

                    <WhySection
                        title="Technical & Banking Integration"
                        subtitle='We don’t just obtain your license — we integrate the full ecosystem:'
                        blocks={[
                            {
                                items: [
                                    { text: "White-label core banking & mobile platforms." },
                                    { text: "Crypto exchange & custody software." },
                                    { text: "Payment gateway integration." },
                                    { text: "AML/Fraud transaction monitoring tools." },
                                    { text: "Corporate bank accounts & PSP onboarding (EU, MENA, Asia)." }
                                ]
                            }
                        ]}
                    />

                    <InfoBlocks
                        title="Launch & Expansion Roadmap"
                        subtitle='Our process ensures smooth licensing & launch:'
                        blocks={[
                            {
                                items: [
                                    { text: "Business model refinement." },
                                    { text: "Jurisdiction analysis & license selection." },
                                    { text: "Company incorporation & compliance setup." },
                                    { text: "License application preparation & submission." },
                                    { text: "Operational readiness: staffing, audit, training." },
                                    { text: "Ongoing compliance & annual reporting." }
                                ]
                            }
                        ]}
                    />

                    <ServiceCards
                        title="Ideal Use Cases"
                        subtitle='The Meta License is best for:'
                        services={[
                            { title: "Neobanks (digital banks)" },
                            { title: "Crypto exchanges & wallets" },
                            { title: "PSPs & payment aggregators" },
                            { title: "Brokerages & CFD trading firms" },
                            { title: "iGaming operators" },
                            { title: "B2B fintech providers" }
                        ]}
                    />

                    <WhySection
                        title="Top Jurisdictions for Fintech & Meta Licensing"
                        blocks={[
                            {
                                items: [
                                    { text: "Lithuania — EU EMI/PI licenses with SEPA." },
                                    { text: "Malta — Crypto + EMI licensing with EU credibility." },
                                    { text: "Mauritius — Fintech-friendly & affordable licensing." },
                                    { text: "Cyprus — PI & investment services licensing." },
                                    { text: "Mwali / Anjouan — Quick approval for startups." },
                                    { text: "Western Sahara (CRA) — DLT, crypto, and Meta Licenses." },
                                    { text: "Estonia — Crypto & VASP (pending new compliance updates)." },
                                    { text: "UAE (DIFC/ADGM) — Regulated fintech sandboxes." }
                                ]
                            }
                        ]}
                    />

                    <FAQList data={faqData} title='Frequently Asked Questions (FAQ)'/>

                    <Testimonials
                        title="Client Testimonials"
                        testimonials={testimonialsData}
                    />

                    <CTA
                        title="Looking for an all-in-one financial license?"
                        description="The Meta License gives your business the power to operate forex, crypto, gaming, and payment services under one regulatory umbrella."
                        buttonText="Book a Free Strategy Call →"
                        buttonLink="/quote"
                    />
                </div>
            </section>
        </main>
    );
};

const faqData = [
    {
        question: "What is a Meta License?",
        answer: "A multi-category license allowing a company to conduct financial services across sectors (e.g., crypto + forex + gaming) under one legal framework.",
    },
    {
        question: "Why is it better than separate licenses?",
        answer: "It reduces cost, complexity, and processing time while giving broader authority.",
    },
    {
        question: "How long does it take?",
        answer: "Depending on jurisdiction: 6–12 weeks for offshore, 3–6 months for EU.",
    },
    {
        question: "What does it cost?",
        answer: "Prices vary by jurisdiction, but packages typically start from USD 25,000+.",
    },
    {
        question: "Can it be used for high-risk industries?",
        answer: "Yes — Meta Licenses are particularly popular with firms in forex, crypto, and gaming.",
    },
];

const testimonialsData = [
    {
        quote: "The Meta License allowed us to launch a combined crypto exchange and forex brokerage under one entity. Offshore Advisory managed the entire process flawlessly.",
        author: "Ahmed S.",
        role: "FinTech Founder"
    }
]